/**
 * PortalDocumentsPage
 *
 * Displays all documents accessible to the investor, grouped by fund.
 * Features:
 * - Header with title and document count
 * - Expandable fund sections (accordion-style)
 * - Document rows with download buttons
 * - Loading, empty, and error states
 */

import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AlertCircle, Clock, FileText, Loader2, Search } from 'lucide-react';
import { useDocuments, useDocumentDownload } from '../hooks/useDocuments';
import { FundDocumentSection } from '../components/FundDocumentSection';
import { GeneralDocumentSection } from '../components/GeneralDocumentSection';
import type { PortalDocument } from '../services/documents-service';

function DocumentsLoading() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Documents</h1>
        <p className="text-muted-foreground mt-1">
          Fund documents and reports available to you
        </p>
      </div>

      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Documents</h1>
        <p className="text-muted-foreground mt-1">
          Fund documents and reports available to you
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Documents Available
          </h3>
          <p className="text-gray-500 text-center max-w-md">
            There are no documents available for your funds at this time. Check
            back later for fund reports, statements, and other documents.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function documentMatchesSearch(doc: PortalDocument, query: string): boolean {
  return (
    doc.name.toLowerCase().includes(query) ||
    doc.original_filename.toLowerCase().includes(query) ||
    doc.tags.some((tag) => tag.toLowerCase().includes(query))
  );
}

function ComingSoonState() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Documents</h1>
        <p className="text-muted-foreground mt-1">
          Fund documents and reports available to you
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Clock className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Coming Soon
          </h3>
          <p className="text-muted-foreground text-center max-w-md">
            Your fund manager hasn&apos;t published documents yet.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export function PortalDocumentsPage() {
  const { data: documentsData, isLoading, error } = useDocuments();
  const { downloadDocument, isDownloading } = useDocumentDownload();
  const [searchQuery, setSearchQuery] = useState('');

  const funds = documentsData?.funds;
  const searchTerm = searchQuery.trim().toLowerCase();
  const initialFolderState = searchTerm ? 'expanded' : 'collapsed';
  const generalDocuments = (documentsData?.general_documents ?? []).filter(
    (doc) => !searchTerm || documentMatchesSearch(doc, searchTerm)
  );

  // Filter documents based on search query (including portfolio company documents)
  const filteredFunds = useMemo(() => {
    if (!funds) return [];
    if (!searchQuery.trim()) return funds;

    const query = searchQuery.toLowerCase();
    return funds
      .map((fund) => {
        // Filter fund-level documents
        const filteredDocs = fund.documents.filter(
          (doc) =>
            doc.name.toLowerCase().includes(query) ||
            doc.original_filename.toLowerCase().includes(query) ||
            doc.tags.some((tag) => tag.toLowerCase().includes(query))
        );

        // Filter portfolio company documents
        const filteredPortfolioCompanies = (fund.portfolio_companies || [])
          .map((pc) => ({
            ...pc,
            documents: pc.documents.filter(
              (doc) =>
                doc.name.toLowerCase().includes(query) ||
                doc.original_filename.toLowerCase().includes(query) ||
                doc.tags.some((tag) => tag.toLowerCase().includes(query)) ||
                pc.portfolio_company_name.toLowerCase().includes(query)
            ),
          }))
          .filter((pc) => pc.documents.length > 0)
          .map((pc) => ({
            ...pc,
            document_count: pc.documents.length,
          }));

        return {
          ...fund,
          documents: filteredDocs,
          document_count: filteredDocs.length,
          portfolio_companies: filteredPortfolioCompanies,
        };
      })
      .filter(
        (fund) =>
          fund.documents.length > 0 || fund.portfolio_companies.length > 0
      );
  }, [funds, searchQuery]);

  const filteredTotalDocuments = useMemo(() => {
    return filteredFunds.reduce((sum, fund) => {
      const fundDocs = fund.document_count;
      const portfolioDocs = (fund.portfolio_companies || []).reduce(
        (pcSum, pc) => pcSum + pc.document_count,
        0
      );
      return sum + fundDocs + portfolioDocs;
    }, generalDocuments.length);
  }, [filteredFunds, generalDocuments.length]);

  if (isLoading) {
    return <DocumentsLoading />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-500 mt-1">
            Fund documents and reports available to you
          </p>
        </div>

        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Loading Documents
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              There was a problem loading your documents. Please try again
              later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (documentsData?.published_fund_count === 0) {
    return <ComingSoonState />;
  }

  if (!documentsData || documentsData.total_documents === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-500 mt-1">
            {documentsData.total_documents}{' '}
            {documentsData.total_documents === 1 ? 'document' : 'documents'}{' '}
            across {documentsData.funds.length}{' '}
            {documentsData.funds.length === 1 ? 'fund' : 'funds'}
          </p>
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Search Results Info */}
      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          {filteredTotalDocuments === 0 ? (
            <>No documents match &quot;{searchQuery}&quot;</>
          ) : (
            <>
              Showing {filteredTotalDocuments}{' '}
              {filteredTotalDocuments === 1 ? 'result' : 'results'} for &quot;
              {searchQuery}&quot;
            </>
          )}
        </p>
      )}

      {/* Fund Document Sections */}
      <div className="space-y-4">
        {generalDocuments.length > 0 && (
          <GeneralDocumentSection
            documents={generalDocuments}
            onDownload={downloadDocument}
            downloadingId={isDownloading}
          />
        )}
        {filteredFunds.map((fund) => (
          <FundDocumentSection
            key={`${fund.fund_id}-${initialFolderState}`}
            fund={fund}
            onDownload={downloadDocument}
            downloadingId={isDownloading}
            initialFolderState={initialFolderState}
          />
        ))}
      </div>

      {/* No search results */}
      {searchQuery && filteredTotalDocuments === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Results Found
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              No documents match your search. Try different keywords or clear
              the search to see all documents.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
